import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchRooms, createRoom, updateRoom, deleteRoom, fetchRoomTypes,
  getRole, getCurrency,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// Front Desk — Slice 1: rooms & housekeeping status board.
const S = {
  en: {
    title: "Rooms & Housekeeping", subtitle: "Track room readiness at a glance",
    manage_types: "Room types", add_room: "Add room", add: "Add", cancel: "Cancel",
    name_ph: "Room no. (e.g. 101)", floor_ph: "Floor (optional)", type_any: "No type",
    no_rooms: "No rooms yet. Add your first room to start the housekeeping board.",
    delete: "Delete", delete_confirm: "Remove this room from the board?",
    floor_label: "Floor", no_floor: "Unassigned",
    group_by: "Group by", group_type: "Room type", group_area: "Area", group_floor: "Floor", no_type: "No type", no_area: "No area",
    st_dirty: "Dirty", st_clean: "Clean", st_inspected: "Ready", st_out_of_order: "Out of order",
    ready_hint: "Ready to sell", oos_reason_q: "Reason it's out of order (optional):",
    oos_category_q: "Category — type one: maintenance / cleaning / other", cat_maintenance: "Maintenance", cat_cleaning: "Cleaning", cat_other: "Other",
    oos_category_label: "Category", oos_detail_label: "Details — what's wrong?", oos_maint_hint: "Visible to the maintenance team.",
    oos_maint_required: "Please describe the maintenance issue.", confirm: "Confirm",
    total: "rooms", set_status: "Set status", could_not_save: "Could not save. Please try again.",
    edit: "Edit", save: "Save", room_type: "Room type",
    filter_all: "All", search_ph: "Search room # or type", no_match: "No rooms match.",
    type_all: "All",
  },
  am: {
    title: "ክፍሎች እና አጽዳት", subtitle: "የክፍሎችን ዝግጁነት በቅጽበት ይከታተሉ",
    manage_types: "የክፍል ዓይነቶች", add_room: "ክፍል ጨምር", add: "ጨምር", cancel: "ሰርዝ",
    name_ph: "የክፍል ቁ. (ለምሳሌ 101)", floor_ph: "ፎቅ (አማራጭ)", type_any: "ዓይነት የለም",
    no_rooms: "እስካሁን ክፍሎች የሉም። የአጽዳት ሰሌዳውን ለመጀመር የመጀመሪያ ክፍልዎን ይጨምሩ።",
    delete: "አጥፋ", delete_confirm: "ይህን ክፍል ከሰሌዳው ላይ ማስወገድ?",
    floor_label: "ፎቅ", no_floor: "ያልተመደበ",
    group_by: "በቡድን", group_type: "የክፍል ዓይነት", group_area: "አካባቢ", group_floor: "ፎቅ", no_type: "ዓይነት የለም", no_area: "አካባቢ የለም",
    st_dirty: "ቆሻሻ", st_clean: "ንጹህ", st_inspected: "ዝግጁ", st_out_of_order: "ከአገልግሎት ውጪ",
    ready_hint: "ለሽያጭ ዝግጁ", oos_reason_q: "ከአገልግሎት ውጪ የሆነበት ምክንያት (አማራጭ):",
    oos_category_q: "Category — type one: maintenance / cleaning / other", cat_maintenance: "Maintenance", cat_cleaning: "Cleaning", cat_other: "Other",
    oos_category_label: "Category", oos_detail_label: "Details — what's wrong?", oos_maint_hint: "Visible to the maintenance team.",
    oos_maint_required: "Please describe the maintenance issue.", confirm: "Confirm", // TODO verify am
    total: "ክፍሎች", set_status: "ሁኔታ አስቀምጥ", could_not_save: "ማስቀመጥ አልተቻለም። እንደገና ይሞክሩ።",
    edit: "አርትዕ", save: "አስቀምጥ", room_type: "የክፍል ዓይነት",
    filter_all: "ሁሉም", search_ph: "የክፍል # ወይም ዓይነት ይፈልጉ", no_match: "የሚዛመድ ክፍል የለም።",
    type_all: "ሁሉም",
  },
  om: {
    title: "Kutaalee fi Qulqullina", subtitle: "Qophii kutaalee daftaan hordofi",
    manage_types: "Gosoota kutaa", add_room: "Kutaa dabali", add: "Dabali", cancel: "Haqi",
    name_ph: "Lakk. kutaa (fkn 101)", floor_ph: "Darbii (filannoo)", type_any: "Gosa hin qabu",
    no_rooms: "Ammatti kutaan hin jiru. Gabatee qulqullinaa jalqabuuf kutaa jalqabaa dabali.",
    delete: "Haqi", delete_confirm: "Kutaa kana gabatee irraa haquu?",
    floor_label: "Darbii", no_floor: "Kan hin ramadamne",
    group_by: "Ramaddii", group_type: "Gosa kutaa", group_area: "Naannoo", group_floor: "Darbii", no_type: "Gosa hin qabu", no_area: "Naannoo hin qabu",
    st_dirty: "Xuraawaa", st_clean: "Qulqulluu", st_inspected: "Qophii", st_out_of_order: "Tajaajila ala",
    ready_hint: "Gurgurtaaf qophii", oos_reason_q: "Sababa tajaajila ala ta'e (filannoo):",
    oos_category_q: "Category — type one: maintenance / cleaning / other", cat_maintenance: "Maintenance", cat_cleaning: "Cleaning", cat_other: "Other",
    oos_category_label: "Category", oos_detail_label: "Details — what's wrong?", oos_maint_hint: "Visible to the maintenance team.",
    oos_maint_required: "Please describe the maintenance issue.", confirm: "Confirm", // TODO verify om
    total: "kutaalee", set_status: "Haala kaa'i", could_not_save: "Kaa'uu hin dandeenye. Irra deebi'i yaali.",
    edit: "Gulaali", save: "Kaa'i", room_type: "Gosa kutaa",
    filter_all: "Hunda", search_ph: "Lakk. kutaa yookaan gosa barbaadi", no_match: "Kutaan walsimu hin jiru.",
    type_all: "Hunda",
  },
};

const STATUSES = ["dirty", "clean", "inspected", "out_of_order"];
const META = {
  dirty: { bg: "#FCEBEB", fg: "#A32D2D", dot: "#D9534F" },
  clean: { bg: "#E6F1FB", fg: "#185FA5", dot: "#3f8cff" },
  inspected: { bg: "#E7F6EC", fg: "#1D7A44", dot: "#22a37e" },
  out_of_order: { bg: "#EFEFF2", fg: "#5b6472", dot: "#8a94a3" },
};
const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };

export default function RoomsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const role = getRole();
  const isManager = role === "manager";
  const cur = getCurrency() || "ETB";

  const [rooms, setRooms] = useState(null);
  const [types, setTypes] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", floor: "", room_type_id: "" });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", floor: "", room_type_id: "" });
  const [err, setErr] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");   // "all" | one of STATUSES
  const [query, setQuery] = useState("");                     // room # / name / type search
  const [typeFilter, setTypeFilter] = useState("all");        // "all" | room_type_name | "__none"
  // Out-of-order inline form: which room's form is open (by id) + its fields.
  // Only one open at a time; opened when "out of order" is chosen for a room.
  const [oosRoomId, setOosRoomId] = useState(null);
  const [oosCategory, setOosCategory] = useState("maintenance");
  const [oosDetail, setOosDetail] = useState("");
  const [oosErr, setOosErr] = useState("");

  // Two-axis layout. X-axis = room type (filter chips); Y-axis = floor (always
  // grouped by floor). Type key — the room's `room_type_name`, or "__none" bucket.
  const typeKey = (r) => (r.room_type_name || "").trim() || "__none";
  // Floor key — prefer the room's own `floor`; otherwise derive one from the
  // leading numeric part of the room number ("R008" → 0, "101" → 1, "1203" → 12).
  // Rooms with no number fall under an "Other" bucket. (Mirrors HousekeepingPage.)
  const floorKey = (r) => {
    if (r.floor != null && String(r.floor).trim() !== "") return String(r.floor).trim();
    const mm = String(r.name || "").match(/\d+/);
    if (!mm) return "__other";
    const num = mm[0];
    return num.length > 2 ? String(parseInt(num.slice(0, -2), 10)) : "0";
  };
  // Rooms are always grouped by floor. Empty/no-floor bucket sorts last.
  const groupLabel = (k) => (k === "__other" ? t("no_floor") : `${t("floor_label")} ${k}`);

  const load = () => fetchRooms().then(setRooms).catch(() => setRooms([]));
  useEffect(() => {
    load();
    fetchRoomTypes().then(setTypes).catch(() => setTypes([]));
  }, []);

  // Direct status change for clean/dirty/inspected. "out_of_order" no longer
  // routes through here — it opens the inline form (openOOO) instead.
  const setStatus = async (room, status) => {
    // optimistic
    setRooms((rs) => rs.map((r) => (r.id === room.id ? { ...r, status } : r)));
    try {
      await updateRoom(room.id, { status });
    } catch {
      load();
    }
  };

  // Open the inline out-of-order form for a room (default category maintenance).
  const openOOO = (room) => {
    setOosRoomId(room.id);
    setOosCategory("maintenance");
    setOosDetail("");
    setOosErr("");
  };
  const cancelOOO = () => {
    setOosRoomId(null);
    setOosErr("");
  };
  const confirmOOO = async (room) => {
    const detail = oosDetail.trim();
    // Detail is required when the category is maintenance (it becomes the
    // maintenance ticket's reason); optional for cleaning / other.
    if (oosCategory === "maintenance" && !detail) {
      setOosErr(t("oos_maint_required"));
      return;
    }
    setOosRoomId(null);
    setOosErr("");
    // optimistic
    setRooms((rs) => rs.map((r) => (r.id === room.id ? { ...r, status: "out_of_order", oos_reason: detail, oos_category: oosCategory } : r)));
    try {
      await updateRoom(room.id, { status: "out_of_order", oos_category: oosCategory, oos_reason: detail });
    } catch {
      load();
    }
  };

  const submitAdd = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return;
    try {
      await createRoom({
        name: form.name.trim(),
        floor: form.floor.trim() || null,
        room_type_id: form.room_type_id ? Number(form.room_type_id) : null,
      });
      setForm({ name: "", floor: "", room_type_id: "" });
      setAdding(false);
      load();
    } catch {
      setErr(t("could_not_save"));
    }
  };

  const startEdit = (room) => {
    setEditId(room.id);
    setEditForm({ name: room.name || "", floor: room.floor || "", room_type_id: room.room_type_id || "" });
  };
  const saveEdit = async () => {
    if (!editForm.name.trim()) return;
    try {
      await updateRoom(editId, {
        name: editForm.name.trim(),
        floor: editForm.floor.trim() || null,
        // 0 clears the type; a positive id assigns it
        room_type_id: editForm.room_type_id ? Number(editForm.room_type_id) : 0,
      });
      setEditId(null);
      load();
    } catch { load(); }
  };

  const removeRoom = async (room) => {
    if (!window.confirm(t("delete_confirm"))) return;
    setRooms((rs) => rs.filter((r) => r.id !== room.id));
    try { await deleteRoom(room.id); } catch { load(); }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: (rooms || []).filter((r) => r.status === s).length }), {});
  // X-axis room-type chips: distinct types present + counts. "__none" bucket last.
  const typeCounts = (rooms || []).reduce((a, r) => { const k = typeKey(r); a[k] = (a[k] || 0) + 1; return a; }, {});
  const typeKeys = Object.keys(typeCounts).sort((a, b) => (a === "__none" ? 1 : b === "__none" ? -1 : a.localeCompare(b)));

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
          <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Link className="btn secondary small" to="/front-desk/room-types">{t("manage_types")}</Link>
          {isManager && <button className="btn primary small" onClick={() => setAdding((v) => !v)}>{t("add_room")}</button>}
        </div>
      </div>

      {/* room-type chips (X) + status filter chips + search */}
      {rooms && rooms.length > 0 && (
        <>
          {/* X-axis: room-type chips (primary) */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 14 }}>
            <span className="muted" style={{ fontSize: 13, alignSelf: "center" }}>{t("room_type")}:</span>
            <span onClick={() => setTypeFilter("all")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", background: typeFilter === "all" ? "#063b48" : "#eef2f2", color: typeFilter === "all" ? "#fff" : "#334", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 600, border: typeFilter === "all" ? "1.5px solid #063b48" : "1.5px solid transparent" }}>
              {t("type_all")} · {rooms.length}
            </span>
            {typeKeys.map((k) => (
              <span key={k} onClick={() => setTypeFilter(k)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", background: typeFilter === k ? "#063b48" : "#eef2f2", color: typeFilter === k ? "#fff" : "#334", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 600, border: typeFilter === k ? "1.5px solid #063b48" : "1.5px solid transparent" }}>
                {k === "__none" ? t("no_type") : k} · {typeCounts[k]}
              </span>
            ))}
          </div>
          {/* Secondary: status chips */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 13, alignSelf: "center" }}>{rooms.length} {t("total")}:</span>
            <span onClick={() => setStatusFilter("all")}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", background: statusFilter === "all" ? "#063b48" : "#eef2f2", color: statusFilter === "all" ? "#fff" : "#334", borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 600, border: statusFilter === "all" ? "1.5px solid #063b48" : "1.5px solid transparent" }}>
              {t("filter_all")} · {rooms.length}
            </span>
            {STATUSES.map((s) => (
              <span key={s} onClick={() => setStatusFilter(s)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", background: META[s].bg, color: META[s].fg, borderRadius: 20, padding: "4px 12px", fontSize: 13, fontWeight: 600, border: statusFilter === s ? `1.5px solid ${META[s].dot}` : "1.5px solid transparent" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: META[s].dot }} /> {counts[s]} {t("st_" + s)}
              </span>
            ))}
          </div>
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("search_ph")}
            style={{ padding: 7, borderRadius: 8, border: "1px solid #d8dee2", fontSize: 13, marginTop: 10, width: 260, maxWidth: "100%", display: "block" }} />
        </>
      )}

      {/* add room form */}
      {adding && isManager && (
        <form onSubmit={submitAdd} style={{ ...CARD, padding: 14, marginTop: 14, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("name_ph")} style={{ padding: 8, borderRadius: 8, border: "1px solid #d8dee2", width: 160 }} autoFocus />
          <input value={form.floor} onChange={(e) => setForm({ ...form, floor: e.target.value })} placeholder={t("floor_ph")} style={{ padding: 8, borderRadius: 8, border: "1px solid #d8dee2", width: 140 }} />
          <select value={form.room_type_id} onChange={(e) => setForm({ ...form, room_type_id: e.target.value })} style={{ padding: 8, borderRadius: 8, border: "1px solid #d8dee2" }}>
            <option value="">{t("type_any")}</option>
            {types.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
          </select>
          <button className="btn primary small" type="submit">{t("add")}</button>
          <button className="btn secondary small" type="button" onClick={() => setAdding(false)}>{t("cancel")}</button>
          {err && <span style={{ color: "#A32D2D", fontSize: 13 }}>{err}</span>}
        </form>
      )}

      {/* board */}
      {rooms == null ? (
        <p className="muted" style={{ marginTop: 14 }}>…</p>
      ) : rooms.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">{t("no_rooms")}</div>
      ) : (() => {
        // Presentational only: apply the active type + status filters + search,
        // then always group the surviving rooms by floor (Y-axis). Rooms are
        // still fetched/created/updated/deleted exactly as before.
        const q = query.trim().toLowerCase();
        const visible = rooms.filter((r) => {
          if (typeFilter !== "all" && typeKey(r) !== typeFilter) return false;
          if (statusFilter !== "all" && r.status !== statusFilter) return false;
          if (!q) return true;
          return `${r.name || ""} ${r.room_type_name || ""}`.toLowerCase().includes(q);
        });
        if (visible.length === 0) {
          return <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">{t("no_match")}</div>;
        }
        const groups = {};
        visible.forEach((r) => { const k = floorKey(r); (groups[k] = groups[k] || []).push(r); });
        const keys = Object.keys(groups).sort((a, b) => {
          if (a === "__other") return 1;
          if (b === "__other") return -1;
          const na = Number(a), nb = Number(b);
          if (!isNaN(na) && !isNaN(nb)) return na - nb;
          return a.localeCompare(b);
        });
        return keys.map((k) => (
          <div key={k} style={{ marginTop: 18 }}>
            <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#8a94a3", marginBottom: 8 }}>
              {groupLabel(k)} · {groups[k].length}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))", gap: 12 }}>
              {groups[k].map((r) => {
                const m = META[r.status] || META.clean;
                if (editId === r.id) {
                  const inp = { padding: 7, borderRadius: 8, border: "1px solid #d8dee2", width: "100%", marginBottom: 8, boxSizing: "border-box" };
                  return (
                    <div key={r.id} style={{ ...CARD, padding: 14, borderLeft: `4px solid ${m.dot}` }}>
                      <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} placeholder={t("name_ph")} style={inp} autoFocus />
                      <input value={editForm.floor} onChange={(e) => setEditForm({ ...editForm, floor: e.target.value })} placeholder={t("floor_ph")} style={inp} />
                      <select value={editForm.room_type_id} onChange={(e) => setEditForm({ ...editForm, room_type_id: e.target.value })} style={{ ...inp, marginBottom: 10 }}>
                        <option value="">{t("type_any")}</option>
                        {types.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
                      </select>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button className="btn primary small" onClick={saveEdit}>{t("save")}</button>
                        <button className="btn secondary small" onClick={() => setEditId(null)}>{t("cancel")}</button>
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={r.id} style={{ ...CARD, padding: 14, borderLeft: `4px solid ${m.dot}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "#063b48" }}>{r.name}</div>
                      <span style={{ background: m.bg, color: m.fg, borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 700 }}>{t("st_" + r.status)}</span>
                    </div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 2, minHeight: 18 }}>
                      {r.room_type_name || "—"}
                      {r.base_rate != null && <> · {formatPrice(r.base_rate, cur)}</>}
                    </div>
                    {r.status === "inspected" && <div style={{ fontSize: 12, color: "#1D7A44", fontWeight: 600 }}>✓ {t("ready_hint")}</div>}
                    {r.status === "out_of_order" && r.oos_reason && <div style={{ fontSize: 12, color: "#5b6472" }}>{r.oos_reason}</div>}

                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                      {STATUSES.map((s) => (
                        <button key={s} onClick={() => (s === "out_of_order" ? openOOO(r) : setStatus(r, s))}
                          style={{
                            fontSize: 12, padding: "4px 9px", borderRadius: 8, cursor: "pointer",
                            border: r.status === s ? `1.5px solid ${META[s].dot}` : "1px solid #e2e6ea",
                            background: r.status === s ? META[s].bg : "#fff",
                            color: r.status === s ? META[s].fg : "#5b6472",
                            fontWeight: r.status === s ? 700 : 500,
                          }}>
                          {t("st_" + s)}
                        </button>
                      ))}
                    </div>

                    {/* Inline out-of-order form (replaces the old window.prompt flow) */}
                    {oosRoomId === r.id && (
                      <div style={{ marginTop: 10, padding: 10, background: "#f7f9f9", border: "1px solid #eef2f2", borderRadius: 10 }}>
                        <label style={{ display: "block", fontSize: 12, color: "#5b6472", marginBottom: 4 }}>{t("oos_category_label")}</label>
                        <select value={oosCategory} onChange={(e) => setOosCategory(e.target.value)}
                          style={{ padding: 7, borderRadius: 8, border: "1px solid #d8dee2", width: "100%", boxSizing: "border-box", fontSize: 13 }}>
                          <option value="maintenance">{t("cat_maintenance")}</option>
                          <option value="cleaning">{t("cat_cleaning")}</option>
                          <option value="other">{t("cat_other")}</option>
                        </select>
                        <label style={{ display: "block", fontSize: 12, color: "#5b6472", marginTop: 8, marginBottom: 4 }}>{t("oos_detail_label")}</label>
                        <textarea value={oosDetail} onChange={(e) => setOosDetail(e.target.value)} rows={2} autoFocus
                          style={{ padding: 7, borderRadius: 8, border: "1px solid #d8dee2", width: "100%", boxSizing: "border-box", fontSize: 13, resize: "vertical" }} />
                        {oosCategory === "maintenance" && <div className="muted" style={{ fontSize: 11.5, marginTop: 4 }}>{t("oos_maint_hint")}</div>}
                        {oosErr && <div style={{ color: "#A32D2D", fontSize: 12, marginTop: 4 }}>{oosErr}</div>}
                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                          <button className="btn primary small" onClick={() => confirmOOO(r)}>{t("confirm")}</button>
                          <button className="btn secondary small" onClick={cancelOOO}>{t("cancel")}</button>
                        </div>
                      </div>
                    )}
                    {isManager && (
                      <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
                        <button onClick={() => startEdit(r)} style={{ fontSize: 12, background: "none", border: "none", color: "#185FA5", cursor: "pointer", padding: 0 }}>{t("edit")}</button>
                        <button onClick={() => removeRoom(r)} style={{ fontSize: 12, background: "none", border: "none", color: "#A32D2D", cursor: "pointer", padding: 0 }}>{t("delete")}</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ));
      })()}
    </div>
  );
}
